import { Form as AntdForm } from 'antd';
import { Children, cloneElement, isValidElement, useEffect } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';

type AntdFormItemProps = React.ComponentProps<typeof AntdForm.Item>;

export type FormItemProps<TFieldValues extends FieldValues = FieldValues> = {
	children: React.ReactNode;
	control: Control<TFieldValues>;
	name: FieldPath<TFieldValues>;
	disabled?: boolean;
	overrideFieldOnChange?: (...values: any[]) => void;
} & Omit<AntdFormItemProps, 'name' | 'rules' | 'validateStatus'>;

// TODO: Support `onBlur` `ref` `reset`
export const FormItem = <TFieldValues extends FieldValues = FieldValues>({
	children,
	control,
	name,
	disabled,
	help,
	valuePropName,
	overrideFieldOnChange,
	...props
}: FormItemProps<TFieldValues>) => {
	const { field, fieldState } = useController({ name, control, disabled });
	
	// Get Ant Design form instance if available
	const form = AntdForm.useFormInstance();

	useEffect(() => {
		// Only call setFieldValue if we have a valid form instance with the method
		if (form && typeof form.setFieldValue === 'function') {
			try {
				form.setFieldValue(name, field.value);
			} catch (error) {
				// Silently handle cases where setFieldValue fails
				console.warn('Failed to set field value:', error);
			}
		}
	}, [field.value, form, name]);

	return (
		<AntdForm.Item
			{...props}
			//@ts-expect-error Ant Design form item name type safe is not necessary here
			name={name}
			initialValue={field.value}
			validateStatus={fieldState.invalid ? 'error' : undefined}
			help={fieldState.error?.message ?? help}
		>
			{Children.map(
				children,
				(child) =>
					isValidElement(child) &&
					cloneElement(child, {
						...field,
						//@ts-expect-error onChange type safe is not necessary here
						onChange: (...params) => {
							try {
								//@ts-expect-error child props type safe is not necessary here
								child.props.onChange && child.props.onChange(...params);
								if (overrideFieldOnChange) {
									const transformedValue = overrideFieldOnChange(...params);
									field.onChange(transformedValue);
								} else {
									field.onChange(...params);
								}
							} catch (error) {
								console.error('FormItem onChange - caught error:', error, 'params:', params);
								// Don't call field.onChange if there was an error
							}
						},
						onBlur: () => {
							//@ts-expect-error child props type safe is not necessary here
							child.props.onBlur && child.props.onBlur();
							field.onBlur();
						},
						...(valuePropName && {
							[valuePropName]: field.value,
						}),
					}),
			)}
		</AntdForm.Item>
	);
};